/**
 * E03-F07-T05 — Internal Notification send dialog. Clone of the legacy
 * openSendEmailDialog (internal_notification branch) + notif-template-toggle +
 * setNotifTemplate:
 *
 *   - a Standard / Confidential template toggle (SegmentedToggle) that swaps the
 *     subject + body (with {{var}} substitution) and highlights the active button;
 *   - the standard template is applied on open (default — acceptance #2);
 *   - editable To / Subject / Body fields;
 *   - toggleable recipient chips (auto-recipients from the defaults endpoint);
 *   - NO CC field (internal_notification has no CC — only the followup does);
 *   - Send fires the universal Stage-5 send (item_key=internal_notification),
 *     and on success the Stage-3 card flips to Sent.
 *
 * Template + substitution decisions are pure (docGenerationModel: applyNotifTemplate
 * / splitTemplate); recipients + templates come from the typed defaults query.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Dialog } from '../ui/Dialog.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { errorMessage } from '../api/client.js';
import { SegmentedToggle } from '../ui/SegmentedToggle.js';
import { useToast } from '../ui/ToastContext.js';
import { INT_NOTIF_TEMPLATE_TYPES, type IntNotifTemplateType } from '@eer/shared';
import { applyNotifTemplate, splitTemplate } from './docGenerationModel.js';
import { useInternalNotificationDefaults, useSendStage5 } from './docGenerationApi.js';

export interface InternalNotificationDialogProps {
  readonly projectId: number;
  readonly open: boolean;
  readonly onClose: () => void;
  /** {{var}} substitution values (clientName / roleName / contactName / …). */
  readonly templateVars?: Readonly<Record<string, string>>;
  /** Fired after a successful send (host flips the card to Sent). */
  readonly onSent?: () => void;
}

const TEMPLATE_OPTIONS = [
  { value: 'standard' as IntNotifTemplateType, label: 'Standard' },
  { value: 'confidential' as IntNotifTemplateType, label: 'Confidential' },
];

export function InternalNotificationDialog({
  projectId,
  open,
  onClose,
  templateVars = {},
  onSent,
}: InternalNotificationDialogProps): JSX.Element {
  const toast = useToast();
  const defaultsQuery = useInternalNotificationDefaults(projectId, open);
  const send = useSendStage5(projectId);

  const [template, setTemplate] = useState<IntNotifTemplateType>('standard');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // Recipient chips: every auto-recipient with a selected flag (toggleable).
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
  // Tracks whether the user hand-edited subject/body (so a toggle doesn't clobber
  // their edits silently — re-applies only when they switch template).
  const recipients = defaultsQuery.data?.recipients ?? [];

  /** Apply a template: substitute vars, split Subject/Body into the fields. */
  function applyTemplate(raw: string): void {
    const substituted = applyNotifTemplate(raw, templateVars);
    const { subject: subj, body: bdy } = splitTemplate(substituted);
    setSubject(subj);
    setBody(bdy);
  }

  // E03-F07-T08: the template is applied ONCE per opening. A background refetch of
  // the defaults (window focus, invalidation) hands back a new `data` object and
  // must not overwrite what the user already typed into Subject/Body or the chips.
  const initializedForOpen = useRef(false);
  useEffect(() => {
    if (!open) initializedForOpen.current = false;
  }, [open]);

  // On open / defaults-load: select all recipients + apply the (default) standard
  // template (acceptance #2 — standard applied by default).
  useEffect(() => {
    if (!open || !defaultsQuery.data || initializedForOpen.current) return;
    initializedForOpen.current = true;
    setSelectedRecipients(new Set(recipients.map((r) => r.email).filter(Boolean)));
    applyTemplate(
      template === 'standard'
        ? defaultsQuery.data.template_standard
        : defaultsQuery.data.template_confidential,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultsQuery.data]);

  /** Template toggle: switch + re-apply the new template's subject/body. */
  function onToggleTemplate(next: IntNotifTemplateType): void {
    setTemplate(next);
    const d = defaultsQuery.data;
    if (d) applyTemplate(next === 'standard' ? d.template_standard : d.template_confidential);
  }

  function toggleRecipient(email: string): void {
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  const toLine = useMemo(
    () =>
      recipients
        .filter((r) => selectedRecipients.has(r.email))
        .map((r) => r.email)
        .join(', '),
    [recipients, selectedRecipients],
  );

  async function onSend(): Promise<void> {
    if (!toLine) {
      toast.error('Select at least one recipient');
      return;
    }
    try {
      await send.mutateAsync({
        item_key: 'internal_notification',
        email: { to: toLine, subject, body, cc: [] },
      });
      toast.success('Kickoff done mail sent');
      onSent?.();
      onClose();
    } catch (error) {
      // PK-QA-33 (sibling of PK-QA-29) — surface the REAL server error (Graph API
      // failure, expired token, network, etc.) instead of a generic 'Send failed'.
      // `errorMessage` reads the thrown `ApiResultError.detail.message` (the same
      // body-parse the rest of the app funnels errors through), falling back to
      // 'Send failed' only when no message is present.
      toast.error(errorMessage(error, 'Send failed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kickoff done mail"
      testId="notif-dialog"
      maxWidth="2xl"
      footer={
        <>
          <button
            type="button"
            data-testid="notif-cancel"
            onClick={onClose}
            className="rounded border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--color-surface-muted)]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="notif-send"
            onClick={() => void onSend()}
            disabled={send.isPending}
            className="rounded bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
          >
            Send
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Template toggle */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-[var(--color-text-strong)]">Template</span>
          <SegmentedToggle<IntNotifTemplateType>
            options={TEMPLATE_OPTIONS}
            value={template}
            onChange={onToggleTemplate}
            ariaLabel="Notification template"
            testId="notif-template-toggle"
          />
        </div>

        {/* Recipient chips (toggleable) — no CC for internal_notification */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-[var(--color-text-strong)]">Recipients</span>
          <div className="flex flex-wrap gap-1.5" data-testid="notif-recipients">
            {recipients.length === 0 && (
              <span className="text-xs text-[var(--color-text-muted)]">
                {defaultsQuery.isLoading ? 'Loading recipients…' : 'No auto-recipients'}
              </span>
            )}
            {recipients.map((r) => {
              const active = selectedRecipients.has(r.email);
              return (
                <button
                  key={r.email}
                  type="button"
                  data-testid="notif-recipient-chip"
                  data-email={r.email}
                  data-active={active ? 'true' : 'false'}
                  aria-pressed={active}
                  onClick={() => toggleRecipient(r.email)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? 'bg-[var(--color-accent)] text-[var(--color-primary)] border-[var(--color-accent)]'
                      : 'bg-white text-[var(--color-text-muted)] border-[var(--color-border)]'
                  }`}
                  title={r.name || r.email}
                >
                  {r.name || r.email}
                </button>
              );
            })}
          </div>
        </div>

        {/* To (editable, derived from chips but free-editable) */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">To</span>
          <input
            type="text"
            data-testid="notif-to"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
            value={toLine}
            readOnly
            aria-label="Recipients line"
          />
        </label>

        {/* Subject */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Subject</span>
          <input
            type="text"
            data-testid="notif-subject"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>

        {/* Body */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Body</span>
          {/* Этап 4 / KS-07: уведомление уходит письмом, поэтому тот же редактор. */}
          <RichTextEditor
            testId="notif-body"
            ariaLabel="Notification body"
            minHeightClass="min-h-[140px]"
            value={body}
            onChange={setBody}
          />
        </label>
      </div>
    </Dialog>
  );
}

/** The selectable template values, re-exported for hosts/tests. */
export const NOTIF_TEMPLATE_TYPES = INT_NOTIF_TEMPLATE_TYPES;
